import React, { useState } from 'react';
import { dashboardService } from '../../services/dashboard';

interface SessionOption {
  id: string;
  status: string;
  startedAt: number | null;
  endedAt: number | null;
}

interface SessionControlsProps {
  selectedSessionId: string | null;
  onSelectSession: (sessionId: string) => void;
  onClearSelection: () => void;
  isLoading?: boolean;
}

const SessionControls: React.FC<SessionControlsProps> = ({
  selectedSessionId,
  onSelectSession,
  onClearSelection,
  isLoading = false
}) => {
  const [sessions, setSessions] = useState<SessionOption[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [sessionsLoaded, setSessionsLoaded] = useState(false);

  const loadSessions = async () => {
    setLoadingSessions(true);
    try {
      const sessionList = await dashboardService.listSessions();
      setSessions(sessionList);
      setSessionsLoaded(true);
    } catch (err) {
      console.error('Failed to load sessions:', err);
    } finally {
      setLoadingSessions(false);
    }
  };

  const formatSessionLabel = (session: SessionOption): string => {
    if (session.startedAt) {
      const date = new Date(session.startedAt);
      return `${session.id.slice(0, 8)}... (${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})`;
    }
    return session.id.slice(0, 12);
  };

  return (
    <div className="rounded-xl dark:bg-claude-darkSurfaceMuted bg-claude-surfaceMuted p-6 border border-claude-border dark:border-claude-darkBorder">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-claude-darkText mb-4">
        Session Selector
      </h3>

      <div className="space-y-4">
        {/* Load Sessions Button */}
        <div>
          <button
            onClick={loadSessions}
            disabled={loadingSessions || isLoading}
            className="w-full px-4 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loadingSessions ? (
              <>
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Loading...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                </svg>
                Load Sessions from DB
              </>
            )}
          </button>
        </div>

        {/* Session Dropdown */}
        {sessionsLoaded && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-claude-darkText mb-2">
                Select a session to view data
              </label>
              <select
                value={selectedSessionId || ''}
                onChange={(e) => {
                  if (e.target.value) {
                    onSelectSession(e.target.value);
                  }
                }}
                disabled={isLoading}
                className="w-full px-3 py-2 border border-gray-300 dark:border-claude-darkBorder rounded-lg text-sm dark:bg-claude-darkBg dark:text-claude-darkText focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <option value="">-- Select Session --</option>
                {sessions.map((session) => (
                  <option key={session.id} value={session.id}>
                    {formatSessionLabel(session)} [{session.status}]
                  </option>
                ))}
              </select>
            </div>

            {/* Clear Selection Button */}
            {selectedSessionId && (
              <button
                onClick={onClearSelection}
                disabled={isLoading}
                className="w-full px-4 py-2 bg-gray-200 dark:bg-slate-700 hover:bg-gray-300 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Clear Selection
              </button>
            )}

            {/* Session Count */}
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {sessions.length} session{sessions.length !== 1 ? 's' : ''} found in database
            </p>
          </>
        )}

        {/* Help Text */}
        {!sessionsLoaded && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Click "Load Sessions" to browse existing monitoring sessions from the database.
          </p>
        )}
      </div>
    </div>
  );
};

export default SessionControls;
